import { IMAGES } from "../lib/site";
import PageHero from "../components/site/PageHero";
import CTASection from "../components/site/CTASection";
import { Building2, ClipboardCheck, PhoneCall, Users } from "lucide-react";

const BENEFITS = [
  {
    icon: Building2,
    title: "One contractor. Every trade.",
    copy: "From tenant repairs to end-of-tenancy overhauls, gardens and clearance — one phone number covers the whole portfolio.",
  },
  {
    icon: ClipboardCheck,
    title: "Even the trades we don't do.",
    copy: "We manage vetted specialists (electrical, gas, roofing) so you don't have to source and coordinate multiple firms.",
  },
  {
    icon: PhoneCall,
    title: "Priority response.",
    copy: "Emergency leaks, break-ins, storm damage — landlord and agency clients move to the front of the queue.",
  },
  {
    icon: Users,
    title: "Tenant-friendly.",
    copy: "Polite, punctual and tidy — the sort of contractor you're happy to send to any of your tenants unannounced.",
  },
];

export default function Landlords() {
  return (
    <>
      <PageHero
        overline="For Landlords & Letting Agents"
        title="Your single, dependable contractor."
        subtitle="One relationship. Every trade. Across the whole property portfolio."
        image={IMAGES.cottage}
        testId="landlords-hero"
      />

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="overline text-neutral-500 mb-6">Why work with us</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Fewer contractors. Fewer headaches. One place the buck stops.
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-neutral-600 text-lg leading-relaxed">
              Managing a portfolio of let properties means juggling gardeners,
              handymen, cleaners, clearance crews and emergency plumbers.
              Bittern Property Services steps in as a single point of contact
              for the lot — including the specialist trades outside our own
              hands. We source, brief, and stand behind the work.
            </p>
            <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
              You get one invoice, one accountable partner, and total
              visibility over what&rsquo;s happening at every address.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                className="bg-white p-10 md:p-12"
                data-testid={`ll-benefit-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Icon size={26} strokeWidth={1.5} />
                <h3 className="mt-6 font-serif-display text-3xl leading-tight">
                  {b.title}
                </h3>
                <p className="mt-4 text-neutral-600 leading-relaxed">
                  {b.copy}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* B2B strip */}
      <section
        className="bg-neutral-950 text-white"
        data-testid="landlords-band"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 fade-up">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <p className="font-serif-display text-6xl leading-none">01</p>
              <p className="mt-4 text-lg">Portfolio contracts</p>
              <p className="mt-2 text-neutral-400 text-sm leading-relaxed">
                Rolling maintenance agreements for multi-property portfolios,
                priced to reflect volume.
              </p>
            </div>
            <div>
              <p className="font-serif-display text-6xl leading-none">02</p>
              <p className="mt-4 text-lg">End-of-tenancy resets</p>
              <p className="mt-2 text-neutral-400 text-sm leading-relaxed">
                Clean, clear, repair and re-list — turned around fast between
                tenancies.
              </p>
            </div>
            <div>
              <p className="font-serif-display text-6xl leading-none">03</p>
              <p className="mt-4 text-lg">Trade coordination</p>
              <p className="mt-2 text-neutral-400 text-sm leading-relaxed">
                Vetted electricians, gas engineers and roofers — organised on
                your behalf.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
